import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../services/firebase/config';
import { authService } from '../services/firebase/auth.service';
import { organizationsService } from '../services/firebase/organizations.service';
import { User, AuthContextType } from '../types/auth.types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [emailVerified, setEmailVerified] = useState(false);
  const [pendingVerification, setPendingVerification] = useState(false);

  useEffect(() => {
    // Listen to Firebase Auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Update email verification status
        setEmailVerified(firebaseUser.emailVerified);

        // User is signed in, get their Firestore document
        // Retry a few times in case document is being created (race condition during signup)
        const maxRetries = 5;
        const retryDelay = 500; // ms

        for (let attempt = 0; attempt < maxRetries; attempt++) {
          try {
            const userDoc = await authService.getUserDocument(firebaseUser.uid);
            if (userDoc) {
              setUser(userDoc);
              setLoading(false);
              return;
            }
            // Document not found, wait and retry
            if (attempt < maxRetries - 1) {
              await new Promise(resolve => setTimeout(resolve, retryDelay));
            }
          } catch (error) {
            console.error('Error fetching user document:', error);
            if (attempt === maxRetries - 1) {
              setUser(null);
            }
          }
        }

        // All retries exhausted
        console.error('User document not found in Firestore after retries');
        setUser(null);
      } else {
        // User is signed out
        setUser(null);
        setEmailVerified(false);
        setPendingVerification(false);
      }
      setLoading(false);
    });

    // Cleanup subscription
    return () => unsubscribe();
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      setLoading(true);
      const firebaseUser = await authService.login(email, password);

      // Check if email is verified
      if (!firebaseUser.emailVerified) {
        setPendingVerification(true);
        setEmailVerified(false);
      } else {
        setPendingVerification(false);
        setEmailVerified(true);
      }
      // onAuthStateChanged will handle updating the user state
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const signup = async (
    email: string,
    password: string,
    displayName: string,
    organizationName: string
  ): Promise<void> => {
    try {
      setLoading(true);

      // First, create the organization
      const tempOrgId = `temp-${Date.now()}`; // Temporary ID for user creation

      // Create user with temporary org ID (we'll update it after org is created)
      const firebaseUser = await authService.signup(email, password, displayName, tempOrgId, 'owner');

      // Now create the actual organization with the user as owner
      const organizationId = await organizationsService.createOrganization(
        organizationName,
        firebaseUser.uid
      );

      // Update user document with the real organization ID
      await authService.updateUserOrganization(firebaseUser.uid, organizationId, 'owner');

      // Sync member to the real organization's members subcollection
      await organizationsService.syncMember(organizationId, firebaseUser.uid, {
        email,
        displayName,
        role: 'owner',
      });

      // Set pending verification since email is not verified yet
      setPendingVerification(true);
      setEmailVerified(false);

      // onAuthStateChanged will handle updating the user state
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await authService.logout();
      setUser(null);
    } catch (error) {
      throw error;
    }
  };

  const updateUserProfile = async (data: Partial<User>): Promise<void> => {
    try {
      if (!user) {
        throw new Error('No user logged in');
      }
      await authService.updateUserProfile(user.uid, data);

      // Update local state
      setUser((prevUser) => (prevUser ? { ...prevUser, ...data } : null));
    } catch (error) {
      throw error;
    }
  };

  const sendVerificationEmail = async (): Promise<void> => {
    try {
      await authService.sendVerificationEmail();
    } catch (error) {
      throw error;
    }
  };

  const checkEmailVerification = async (): Promise<boolean> => {
    try {
      const verified = await authService.checkEmailVerified();
      setEmailVerified(verified);
      if (verified) {
        setPendingVerification(false);
      }
      return verified;
    } catch (error) {
      throw error;
    }
  };

  const clearPendingVerification = (): void => {
    setPendingVerification(false);
  };

  const value: AuthContextType = {
    user,
    loading,
    emailVerified,
    pendingVerification,
    login,
    signup,
    logout,
    updateUserProfile,
    sendVerificationEmail,
    checkEmailVerification,
    clearPendingVerification,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Custom hook to use the auth context
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
